import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import { theme } from "antd";
import Quill from "quill";
import { useId, useLayoutEffect, useRef, useState } from "react";
import "./Editor.scss";

export interface EditorProps {
  readonly className?: string;
  readonly inline?: boolean;
  readonly readOnly?: boolean;
  readonly rows?: number | "auto";
  readonly bordered?: boolean;
  readonly placeholder?: string;
  readonly value?: string;
  // Show the full WYSIWYG toolbar (headings, bold/italic/underline/strike, lists, blockquote, link, image).
  // Images are inserted by URL (see below) rather than embedded, so the stored HTML stays lean.
  readonly richToolbar?: boolean;
  readonly onChange?: (value: string) => void;
  readonly onBlur?: (value: string) => void;
  readonly onFocus?: (value: string) => void;
}

// Baseline long-form formatting set for rich content (e.g. an Article material body).
const RICH_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["blockquote", "link", "image"],
  ["clean"],
];

// Insert an image by URL (admins host the image on a public link) instead of Quill's default base64 embed,
// which would bloat the stored HTML. `this` is the Quill toolbar module, exposing the editor as `this.quill`.
function imageByUrlHandler(this: any): void {
  const url = window.prompt("Image URL (paste a public link to the image):");
  if (!url) return;
  const range = this.quill.getSelection(true);
  this.quill.insertEmbed(range.index, "image", url, "user");
  this.quill.setSelection(range.index + 1, 0);
}

export function Editor(props: EditorProps) {
  const ref = useRef<HTMLDivElement>(null!);
  const quillRef = useRef(null);
  const [editor, setEditor] = useState<Quill | null>(null);
  const { token } = theme.useToken();

  // Lifecycle

  useLayoutEffect(() => {
    if (quillRef.current) return;

    const useRichToolbar =
      props.richToolbar && !props.inline && !props.readOnly;

    const editor = new Quill(ref.current, {
      // debug: "info",
      placeholder: props.placeholder,
      modules: {
        toolbar: useRichToolbar
          ? { container: RICH_TOOLBAR, handlers: { image: imageByUrlHandler } }
          : !props.inline && !props.readOnly,
        keyboard: {
          bindings: {
            enter: {
              key: 13,
              handler: () => {
                return !props.inline;
              },
            },
          },
        },
        clipboard: {
          newLines: !props.inline,
        },
      },
      theme: "snow",
    });
    (quillRef as any).current = editor;
    setEditor(editor);

    editor.clipboard.dangerouslyPasteHTML(props.value ?? "");

    return () => {
      quillRef.current = null;
      setEditor(null);
    };
  }, []);

  useLayoutEffect(() => {
    if (editor) {
      const html = getHtml();
      if (props.value !== html) {
        editor.clipboard.dangerouslyPasteHTML(props.value ?? "");
      }
    }
  }, [editor, props.value]);

  useLayoutEffect(() => {
    const onChange = () => props.onChange!(getHtml());
    const onBlur = () => props.onBlur!(getHtml());
    const onFocus = () => props.onFocus!(getHtml());

    props.onChange && editor?.on("text-change", onChange);
    props.onFocus && editor?.root.addEventListener("focus", onFocus);
    props.onBlur && editor?.root.addEventListener("blur", onBlur);

    return () => {
      props.onChange && editor?.off("text-change", onChange);
      props.onFocus && editor?.root.removeEventListener("focus", onFocus);
      props.onBlur && editor?.root.removeEventListener("blur", onBlur);
    };
  });

  // Methods

  function getHtml(): string {
    const html =
      (editor?.getLength() || 0) > 1 ? editor?.root.innerHTML || "" : "";

    return html;
  }

  return (
    <div
      className={`my-editor flex flex-col overflow-hidden ${props.inline ? "my-inline-editor-readonly" : ""} ${props.className}`}
      style={{
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor:
          !props.inline && !props.readOnly
            ? token.colorPrimaryBorder
            : "transparent",
        borderRadius: token.borderRadius,
        pointerEvents: props.readOnly ? "none" : "initial",
      }}
    >
      <div
        ref={ref}
        style={{
          minHeight:
            typeof props.rows === "number"
              ? 16 * (props.rows || 1)
              : props.rows,
        }}
      ></div>
    </div>
  );
}

export default Editor;
